import { z } from 'zod';

import { ProductKeySchema } from '../../billing/product-key/product-key';
import { UuidSchema } from '../../common/id/id';
import { DiscountSourceSchema } from '../discount-source/discount-source';
import { DiscountTypeSchema } from '../discount-type/discount-type';
import { StackingPolicySchema } from '../stacking-policy/stacking-policy';

export const DISCOUNT_RULE_NAME_MAX_LENGTH = 160;

/** How many products one rule may be scoped to before it should just be "all". */
export const DISCOUNT_RULE_MAX_PRODUCTS = 50;

/**
 * The bounds an authoring surface has to hold its own controls to.
 *
 * Named for `TEST_ATTEMPTS_MIN`'s reason: an editor clamps a number box and
 * words a hint from these, and every one of those spelled `3650` is a place the
 * contract can be tightened without the client noticing — a control that offers
 * a value the api refuses, or refuses one it would take.
 */
export const DISCOUNT_VALIDITY_DAYS_MIN = 1;
/** Ten years. A grant valid longer than that is a grant that never expires. */
export const DISCOUNT_VALIDITY_DAYS_MAX = 3650;
export const DISCOUNT_MAX_PER_USER_MIN = 1;
export const DISCOUNT_MAX_PER_USER_MAX = 100;

/** `null` means the grant never expires (matrix row 14). */
export const DiscountValidityDaysSchema = z
  .number()
  .int()
  .min(DISCOUNT_VALIDITY_DAYS_MIN)
  .max(DISCOUNT_VALIDITY_DAYS_MAX)
  .nullable();

/**
 * How many grants one member may ever hold from one rule.
 *
 * The floor is 1, which is what makes matrix row 9 true: passing a test a
 * second time issues nothing, because the member already holds their one grant.
 * Retaking for a better score stays allowed and is simply grant-neutral.
 */
export const DiscountMaxPerUserSchema = z
  .number()
  .int()
  .min(DISCOUNT_MAX_PER_USER_MIN)
  .max(DISCOUNT_MAX_PER_USER_MAX);

/**
 * What a completion or a pass is worth (`03-courses-discounts.md` §Grant issuance).
 *
 * **The rule's `name` is not translated, and that is deliberate.** N5 governs
 * member-facing content; a rule name is read by operators in the admin list and
 * never rendered to a member — what a member sees is the *grant*, described by
 * its value, its products and its expiry. A translation table here would be two
 * rows of staff shorthand per rule for nobody's benefit.
 */
export const DiscountRuleSchema = z
  .object({
    id: UuidSchema,
    name: z.string(),
    source: DiscountSourceSchema,
    /** The course (for `course_completion`) or the test (for `test_pass`). */
    sourceId: UuidSchema,
    /**
     * That course's or test's title, in the reader's locale — what lets a rule
     * row say which thing it pays for instead of drawing `sourceId`.
     *
     * Resolved for a whole page in **one query per source kind** (P22b3 built
     * `CourseRepository.titlesFor` for `courseTitle`; this is the same shape a
     * second and third time), never one lookup per row, which is the N+1 W6
     * forbids.
     *
     * `null` means the title lookup came back short — a soft-deleted course or
     * test, or one with no translation in this locale. It never means "no
     * source": `sourceId` is a notNull discriminated reference, so every rule
     * has one. A row whose lookup came back short is still a row (P14b), and the
     * list falls back to naming the *kind*.
     */
    sourceName: z.string().nullable(),
    discountType: DiscountTypeSchema,
    value: z.number(),
    /**
     * Which products this may reduce. **Empty means all of them** (matrix row 19).
     *
     * Empty-as-all rather than a separate `appliesToAll` boolean: two fields
     * would have four states, two of which ("all, but also this list") mean
     * nothing, and somebody would eventually write one.
     */
    appliesToProductKeys: z.array(ProductKeySchema),
    validityDays: DiscountValidityDaysSchema,
    stackingPolicy: StackingPolicySchema,
    maxPerUser: DiscountMaxPerUserSchema,
    isActive: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type DiscountRule = z.infer<typeof DiscountRuleSchema>;
